import { Injectable, inject } from '@angular/core';
import { selectCart } from '@mkp/cart/state';
import { selectMetadatasEntities } from '@mkp/metadata/state';
import { NotificationGatewayActions } from '@mkp/notification/state/actions';
import { productOptionsPageActions } from '@mkp/product-options/actions';
import {
  selectDisplayedProducts,
  selectProducts,
  selectSelectedPlatformGroup,
} from '@mkp/product-options/state';
import { productSummaryApiActions, productSummaryPageActions } from '@mkp/product-summary/actions';
import { ProductOfferingActions } from '@mkp/product/feature-product-offering/actions';
import { ProductPlatformGroup } from '@mkp/shared/models';
import { getPublicationDaysFromProductCode } from '@mkp/shared/util-product';
import { EcommercePlatformType } from '@mkp/tracking/models';
import { TrackingActions } from '@mkp/tracking/state/actions';
import { TrackingService } from '@mkp/tracking/util-tracking';
import { Vacancy } from '@mkp/vacancy/models';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { exhaustMap, filter, map, mergeMap, take } from 'rxjs';
import { getVacancyById, selectVacancyFromRoute } from './vacancy.selectors';

@Injectable()
export class VacancyTrackingEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly trackingService = inject(TrackingService);

  /**
   * @description ecom product scroll event
   * This effect detects when a user clicks the scroll button on the job publication page to see other products
   */
  readonly ecommerceProductScrollEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(productOptionsPageActions.scrollProducts),
      map(({ direction }) => this.trackingService.getProductScrollEvent(direction)),
      map(TrackingActions.sendPureFrontendEvent)
    )
  );

  /**
   * @description ecom Purchase event
   * This effect detects when a user purchases a new product, and dispatches that event
   */
  readonly ecommercePurchaseEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(productSummaryApiActions.productCheckoutSuccess),
      filter(Boolean),
      concatLatestFrom(({ cartCheckout }) => [
        this.store.select(selectMetadatasEntities),
        this.store.select(getVacancyById(cartCheckout.vacancyId)).pipe(filter(Boolean)),
        this.store.select(selectProducts),
      ]),
      map(([cartState, metadata, vacancy, products]) => {
        const productCode = cartState.cart.items[0]?.variant?.product?.code;
        const publicationDays = getPublicationDaysFromProductCode(productCode, products);
        return [
          this.trackingService.getEcommerceEvent({
            eventName: 'ecommerce_purchase',
            ecommerce: { cart: cartState.cart, funnelName: 'ecommerce', action: 'purchase' },
            jobAd: { vacancy, metadata, publicationDays },
            promotionCart: cartState.cart,
          }),
          this.trackingService.getJobAdCreatedEvent({ vacancy, metadata, publicationDays }),
        ];
      }),
      mergeMap((trackingData) => trackingData.map(TrackingActions.sendPureFrontendEvent))
    )
  );

  /**
   * @description ecom Unverified purchase event
   * This effect detects when an unverified user purchases a new product, and dispatches that event
   */
  readonly ecommerceUnverifiedPurchaseEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationGatewayActions.unverifiedCheckoutSuccess),
      concatLatestFrom(() => [
        this.store.select(selectMetadatasEntities),
        this.store.select(selectProducts),
      ]),
      map(([{ cart, vacancy }, metadata, products]) => {
        const productCode = cart.items[0]?.variant?.product?.code;
        const publicationDays = getPublicationDaysFromProductCode(productCode, products);
        return this.trackingService.getUnverifiedCheckoutEvent({
          jobAd: { vacancy, metadata, publicationDays },
          ecommerce: { cart, funnelName: 'ecommerce' },
        });
      }),
      map(TrackingActions.sendPureFrontendEvent)
    )
  );

  /**
   * @description ecom Cart Add event
   * This effect detects when a user selects a product, and dispatches that event
   */
  readonly ecommerceCartAdd$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductOfferingActions.productSelected),
      concatLatestFrom(() => [
        this.store.select(selectSelectedPlatformGroup),
        this.store.select(selectProducts),
        this.store.select(selectMetadatasEntities),
        this.store.select(selectVacancyFromRoute).pipe(filter(Boolean)),
      ]),
      map(([{ productCode }, platformGroup, products, metadata, vacancy]) => {
        const product = products?.find((p) => p.code === productCode);
        const selectedProducts = product ? [product] : [];
        const platform = getPlatform(platformGroup);
        const publicationDays = getPublicationDaysFromProductCode(productCode, products);
        return this.trackingService.getEcommerceEvent({
          ecommerce: {
            products: selectedProducts,
            funnelName: 'ecommerce',
            platform,
            action: 'add',
          },
          jobAd: { vacancy, metadata, publicationDays },
          promotionCart: undefined,
          eventName: 'ecommerce_cart_add',
        });
      }),
      map(TrackingActions.sendPureFrontendEvent)
    )
  );

  /**
   * @description ecom Cart Remove event
   * This effect detects when a user wants to change a product, and dispatches that event
   */
  readonly ecommerceCartRemove$ = createEffect(() =>
    this.actions$.pipe(
      ofType(productSummaryPageActions.changeProduct),
      concatLatestFrom((state) => [
        this.store.select(selectMetadatasEntities),
        this.store.select(selectCart).pipe(filter(Boolean)),
        this.store.select(getVacancyById(state.vacancyId)).pipe(filter(Boolean)),
        this.store.select(selectProducts),
      ]),
      map(([, metadata, cart, vacancy, products]) => {
        const productCode = cart.items[0]?.variant?.product?.code;
        const publicationDays = getPublicationDaysFromProductCode(productCode, products);
        return this.trackingService.getEcommerceEvent({
          ecommerce: { cart, funnelName: 'ecommerce', action: 'remove' },
          jobAd: { vacancy, metadata, publicationDays },
          promotionCart: cart,
          eventName: 'ecommerce_cart_remove',
        });
      }),
      map(TrackingActions.sendPureFrontendEvent)
    )
  );

  /**
   * @description ecom Product List View event
   * This effect detects when a user land to product options list, and dispatches that event
   */
  readonly ecommerceProductListView$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        productOptionsPageActions.loadProductList,
        productOptionsPageActions.switchPlatformGroup
      ),

      map(({ platformGroup }) => getPlatform(platformGroup)),
      concatLatestFrom(() => [this.store.select(selectVacancyFromRoute)]),
      filter(hasPlatformAndVacancy),
      concatLatestFrom(() => [this.store.select(selectMetadatasEntities)]),
      exhaustMap(([[platform, vacancy], metadata]) =>
        this.store.select(selectDisplayedProducts(vacancy.id)).pipe(
          filter((products) => products?.length > 0),
          map((products) => [platform, vacancy, products, metadata] as const),
          take(1)
        )
      ),
      map(([platform, vacancy, products, metadata]) => {
        const stepSuffix = ' - Product Selection';

        return this.trackingService.getEcommerceEvent({
          ecommerce: { products, funnelName: 'ecommerce', platform, action: 'detail' },
          jobAd: { vacancy, metadata },
          promotionCart: undefined,
          eventName: 'ecommerce_product_list_view',
          pageSuffix: stepSuffix,
        });
      }),
      map(TrackingActions.sendPureFrontendEvent)
    )
  );

  /**
   * @description ecommerce boost purchase event
   * This effect tracks when a user clicks the boost button
   */
  readonly ecommerceBoostEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TrackingActions.trackBoostButtonClick),
      concatLatestFrom(() => [
        this.store.select(selectVacancyFromRoute).pipe(filter(Boolean)),
        this.store.select(selectMetadatasEntities),
      ]),
      map(([, vacancy, metadata]) =>
        this.trackingService.getBoostButtonEvent({ vacancy, metadata })
      ),
      map(TrackingActions.sendPureFrontendEvent)
    )
  );
}

const getPlatform = (
  platformGroup: ProductPlatformGroup | null
): EcommercePlatformType | undefined => {
  if (!platformGroup) {
    return undefined;
  }

  switch (platformGroup) {
    case ProductPlatformGroup.JobScout24:
      return 'jobs24';
    case ProductPlatformGroup.JobUp:
      return 'jobup';
    case ProductPlatformGroup.Jobs:
    default:
      return 'jobs';
  }
};

const hasPlatformAndVacancy = (
  input: [EcommercePlatformType | undefined, Vacancy | undefined]
): input is [EcommercePlatformType, Vacancy] => input[0] !== undefined && input[1] !== undefined;
